import { useState } from "react";

import { XCircle } from "lucide-react";
import { useDispatch } from "react-redux";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  useBundleSplitterUpload,
  useClassifyFiles,
} from "@/hooks/mutations/use-bundle-splitter";
import { getFriendlyErrorMessage, notifyError } from "@/utils/notification";

import { FilePreview } from "../FilePreview";
import { extractTaskIds } from "../utils";

type UploadPhaseProps = {
  file: File;
  userId: string;
  caseNumber: string;
  onTaskCreated: (taskId: string) => void;
  onDismiss?: () => void;
};

export const UploadPhase = ({
  file,
  userId,
  caseNumber,
  onTaskCreated,
  onDismiss,
}: UploadPhaseProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isClassifying, setIsClassifying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dispatch = useDispatch();

  const bundleSplitterUpload = useBundleSplitterUpload();
  const {
    mutateAsync: classifyFiles,
    isPending: isClassifyPending,
    isSuccess: isClassifySuccess,
    isError: isClassifyError,
  } = useClassifyFiles();

  const handleClassify = async () => {
    try {
      setIsUploading(true);
      setErrorMessage(null);
      setUploadProgress(0);

      const filePath = await bundleSplitterUpload.uploadFile(
        file,
        caseNumber,
        (progress) => setUploadProgress(progress)
      );
      setIsUploading(false);
      setIsClassifying(true);

      const requestData = {
        file_paths: [filePath],
        user_id: userId,
        case_number: caseNumber,
      };

      const response = await classifyFiles(requestData);
      const extractedTaskIds = extractTaskIds(response);

      if (extractedTaskIds.length > 0) {
        onTaskCreated(extractedTaskIds[0]);
      } else {
        throw new Error("No task ID returned from classification");
      }
    } catch (err) {
      console.error("Error processing file:", err);
      notifyError(err, dispatch);
      setErrorMessage(getFriendlyErrorMessage(err));
      setIsUploading(false);
      setIsClassifying(false);
      setUploadProgress(0);
    }
  };

  const isPending =
    isUploading || bundleSplitterUpload.isLoading || isClassifyPending;
  const hasError = !!(bundleSplitterUpload.error || isClassifyError);

  return (
    <>
      {errorMessage && (
        <Alert variant="destructive" className="mb-3">
          <XCircle />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <FilePreview
        file={file}
        isSuccess={isClassifySuccess}
        isFailed={hasError}
        isLoading={isPending}
        uploadProgress={isUploading ? uploadProgress : undefined}
        onClear={!isPending ? onDismiss : undefined}
      />

      {isClassifying && (
        <div className="space-y-1.5">
          <Progress className="h-2 animate-pulse" />
          <p className="text-center text-xs text-foreground-muted">
            Preparing Classification
          </p>
        </div>
      )}

      {isUploading ? (
        <p className="text-center text-xs text-foreground-muted">
          {uploadProgress > 0
            ? `Uploading File (${Math.round(uploadProgress)}%)`
            : "Uploading File"}
        </p>
      ) : (
        !isClassifyPending &&
        !isClassifying && (
          <Button
            onClick={handleClassify}
            isLoading={isPending}
            disabled={isPending}
          >
            Continue to Processing
          </Button>
        )
      )}
    </>
  );
};
